import { useEffect, useMemo, useState } from "react";
import { legalActions, type HandState, type LegalAction } from "@joker-holdem/core";
import type { PresetContext } from "./betPresets.js";

/** The two `LegalAction` variants that carry a `min`/`max` legal range. */
type OpenAction = Extract<LegalAction, { type: "bet" | "raise" }>;

export interface BetAmountState {
  readonly legal: readonly LegalAction[];
  readonly fold: Extract<LegalAction, { type: "fold" }> | undefined;
  readonly check: Extract<LegalAction, { type: "check" }> | undefined;
  readonly call: Extract<LegalAction, { type: "call" }> | undefined;
  /** The currently-open bet/raise action (whichever is legal), if any. */
  readonly open: OpenAction | undefined;
  readonly betValue: number;
  readonly setBetValue: (value: number) => void;
  readonly presetCtx: PresetContext;
}

const EMPTY_PRESET_CTX: PresetContext = {
  street: "preflop",
  facingBet: false,
  oppBet: 0,
  pot: 0,
  bbChips: 2,
  allInTo: 0,
};

/**
 * Bet-amount state shared between `ActionBar` (presets + action buttons)
 * and `PlayScreen` (the amount input itself, now rendered inside the
 * hero's `PlayerPanel` -- 2026-07-17 フィードバック #3). Both need the same
 * `betValue`, so it's lifted into this one hook, called once per render in
 * `PlayScreen` and threaded down as props, rather than each component
 * keeping its own (which would let them drift out of sync).
 *
 * Accepts `hand: null` (rather than requiring callers to skip the call
 * conditionally) so `PlayScreen` can call this unconditionally on every
 * render, before its own `if (!hand) return null` -- conditionally calling
 * a hook based on `hand`'s presence would violate the rules of hooks.
 */
export function useBetAmount(hand: HandState | null): BetAmountState {
  const legal = useMemo(() => (hand ? legalActions(hand) : []), [hand]);
  const fold = legal.find((a) => a.type === "fold");
  const check = legal.find((a) => a.type === "check");
  const call = legal.find((a) => a.type === "call");
  const bet = legal.find((a) => a.type === "bet");
  const raise = legal.find((a) => a.type === "raise");
  const allIn = legal.find((a) => a.type === "allIn");
  const open = bet ?? raise;

  const [betValue, setBetValue] = useState<number>(open?.min ?? 0);

  useEffect(() => {
    if (open) setBetValue(open.min);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset keyed on the hand object identity, not `open` (a fresh object every render).
  }, [hand]);

  if (!hand) {
    return { legal, fold, check, call, open, betValue, setBetValue, presetCtx: EMPTY_PRESET_CTX };
  }

  const oppSeat = hand.toAct === 0 ? 1 : 0;
  const presetCtx: PresetContext = {
    street: hand.street,
    facingBet: !!call,
    oppBet: hand.players[oppSeat].bet,
    pot: hand.players[0].totalCommitted + hand.players[1].totalCommitted,
    bbChips: hand.config.bbChips,
    allInTo: allIn?.amount ?? 0,
  };

  return { legal, fold, check, call, open, betValue, setBetValue, presetCtx };
}
